import React, { useEffect, useState } from "react";
import { FiSun, FiMoon } from "react-icons/fi";
import { FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import SummaryApi from "../common";

const ThemeSwitcher = () => {
  const [isDark, setIsDark] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [notifications, setNotifications] = useState("on");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ SINGLE EFFECT: Apply theme on mount & sync with localStorage + system preference
  useEffect(() => {
    const initializeTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;

      const shouldBeDark =
        savedTheme === "dark" || (!savedTheme && prefersDark);

      // Apply to document
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
        setIsDark(true);
      } else {
        document.documentElement.classList.remove("dark");
        setIsDark(false);
      }
    };

    initializeTheme();

    // Listen to system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e) => {
      if (!localStorage.getItem("theme")) {
        if (e.matches) {
          document.documentElement.classList.add("dark");
          setIsDark(true);
        } else {
          document.documentElement.classList.remove("dark");
          setIsDark(false);
        }
      }
    };

    // Listen for theme change events from sidebar or other tabs
    const handleThemeChange = () => {
      const isCurrentlyDark =
        document.documentElement.classList.contains("dark");
      setIsDark(isCurrentlyDark);
    };

    // ✅ Sync across tabs using storage event
    const handleStorageChange = (e) => {
      if (e.key === "theme") {
        const newTheme = e.newValue;
        if (newTheme === "dark") {
          document.documentElement.classList.add("dark");
          setIsDark(true);
        } else {
          document.documentElement.classList.remove("dark");
          setIsDark(false);
        }
        window.dispatchEvent(new Event("themeChanged"));
      }
    };

    // Add all event listeners
    mediaQuery.addEventListener("change", handleSystemThemeChange);
    window.addEventListener("themeChanged", handleThemeChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Toggle theme
  const toggleTheme = (mode) => {
    const newIsDark = mode === "dark";
    if (newIsDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
      setIsDark(true);
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
      setIsDark(false);
    }
    window.dispatchEvent(new Event("themeChanged"));
  };

  // Password update API function
  const updateStudentPassword = async (email, newPassword) => {
    try {
      setIsLoading(true);

      const payload = {
        email: email,
        newPassword: newPassword,
      };

      const response = await fetch(SummaryApi.studentUpdateForgotPassword.url, {
        method: SummaryApi.studentUpdateForgotPassword.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating password:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    // Get student email from localStorage
    const studentEmail = localStorage.getItem("student_email");

    if (!studentEmail) {
      toast.error("User email not found. Please log in again.");
      return;
    }

    try {
      setIsLoading(true);
      await updateStudentPassword(studentEmail, newPassword);

      toast.success("Password changed successfully!");
      setShowModal(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password update error:", error);
      toast.error("Failed to update password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // 🎨 Common class for options to avoid repetition
  const optionClass = "bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-white";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-4 sm:py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <h1 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-10 text-gray-800 dark:text-gray-100 text-center">
        Settings
      </h1>

      {/* Settings Rows */}
      <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-3xl">
        {/* Mode Row */}
        <div className="grid grid-cols-1 sm:grid-cols-[200px_30px_1fr] items-center w-full gap-2 sm:gap-0">
          <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm sm:text-base">
            Mode
          </div>
          <div className="hidden sm:block text-gray-800 dark:text-gray-200 text-center">
            :
          </div>
          <select
            value={isDark ? "dark" : "light"}
            onChange={(e) => toggleTheme(e.target.value)}
            className="px-3 sm:px-4 py-1 sm:py-1 border rounded-md bg-gray-50 dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm sm:text-base"
          >
            {/* Added styling to options */}
            <option value="light" className={optionClass}>
              ☀️ Light Mode
            </option>
            <option value="dark" className={optionClass}>
              🌙 Dark Mode
            </option>
          </select>
        </div>

        {/* Notifications Row */}
        <div className="grid grid-cols-1 sm:grid-cols-[200px_30px_1fr] items-center w-full gap-2 sm:gap-0">
          <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm sm:text-base">
            Notifications
          </div>
          <div className="hidden sm:block text-gray-800 dark:text-gray-200 text-center">
            :
          </div>
          <select
            value={notifications}
            onChange={(e) => setNotifications(e.target.value)}
            className="px-3 sm:px-4 py-1 sm:py-1 border rounded-md bg-gray-50 dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm sm:text-base"
          >
             {/* Added styling to options */}
            <option value="on" className={optionClass}>
              🔔 Turn On
            </option>
            <option value="off" className={optionClass}>
              🔕 Turn Off
            </option>
          </select>
        </div>

        {/* Change Password Row */}
        <div className="grid grid-cols-1 sm:grid-cols-[200px_30px_1fr] items-center w-full gap-2 sm:gap-0">
          <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm sm:text-base">
            Change Password
          </div>
          <div className="hidden sm:block text-gray-800 dark:text-gray-200 text-center">
            :
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="text-blue-600 dark:text-blue-600 font-semibold hover:underline text-left text-sm sm:text-base"
          >
            Click here
          </button>
        </div>
      </div>

      {/* Password Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md shadow-lg border border-gray-200 dark:border-gray-700 relative">
            {/* Close Icon */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition duration-200"
              disabled={isLoading}
            >
              <FaTimes className="text-lg" />
            </button>

            <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-900 dark:text-white text-center">
              Change Password
            </h2>
            <form
              onSubmit={handlePasswordSubmit}
              className="space-y-4 sm:space-y-6"
            >
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full py-1 sm:py-3 px-3 sm:px-4 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 dark:text-white text-sm sm:text-base disabled:opacity-50"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                    tabIndex={-1}
                    disabled={isLoading}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full py-1 sm:py-3 px-3 sm:px-4 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 dark:text-white text-sm sm:text-base disabled:opacity-50"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                    tabIndex={-1}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Update Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-1 sm:py-2 rounded-md bg-blue-900 hover:bg-blue-700  dark:bg-blue-600 text-white dark:text-white hover:opacity-90 transition duration-200 text-sm sm:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white dark:border-black border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default ThemeSwitcher;